/**
 * Users Page (Settings)
 * List, create, edit, deactivate/reactivate, and reset passwords for the
 * tenant's users.
 *
 * Visible to ADMIN and OFFICE_MANAGER (canManageUsers). Per-row controls follow
 * canManageUserRole, which mirrors the backend RBAC rule: an ADMIN can manage
 * anyone, an OFFICE_MANAGER only TECHNICIAN / CUSTOMER_PORTAL users and can
 * only assign those roles. These checks are UX only; the backend enforces them
 * and its message (INSUFFICIENT_PERMISSIONS, LAST_ADMIN, DUPLICATE_USER, ...)
 * is shown when it refuses.
 *
 * Users are identified by email (the backend User model has no name).
 * Reactivation is a PATCH { isActive: true }; the backend has no dedicated route.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAPIClient, IUsersApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { assignableRolesFor, canManageMfaPolicy, canManageUserRole, canManageUsers } from '../../utils/permissions';
import { getPasswordStrengthError, PASSWORD_REQUIREMENTS_HINT } from '../../utils/password';
import {
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  rowActionClass,
  rowDangerActionClass,
} from '../../components/formStyles';
import { Toast } from '../../components/Toast';
import { UserMfaPanel } from '../../components/UserMfaPanel';

const ROLE_LABELS: Record<types.UserRole, string> = {
  [types.UserRole.ADMIN]: 'Admin',
  [types.UserRole.OFFICE_MANAGER]: 'Office Manager',
  [types.UserRole.OFFICE]: 'Office (legacy)',
  [types.UserRole.TECHNICIAN]: 'Technician',
  [types.UserRole.CUSTOMER_PORTAL]: 'Customer Portal',
};

function roleLabel(role: string): string {
  return ROLE_LABELS[role as types.UserRole] ?? role;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type StatusFilter = 'all' | 'active' | 'inactive';

interface FormState {
  email: string;
  password: string;
  role: types.UserRole | '';
}

type Editing = { mode: 'create' } | { mode: 'edit'; target: types.UserListItem } | null;

/** A row awaiting confirmation of a status toggle or password reset. */
type RowAction = { kind: 'toggleActive' | 'resetPassword'; userId: string } | null;

const badgeBase = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';

export function UsersPage() {
  const { user } = useAuth();
  const apiClient = React.useMemo<IUsersApi>(() => createAPIClient(), []);
  const canAccess = canManageUsers(user);
  const actorRole = user?.role;
  const assignableRoles = assignableRolesFor(actorRole);

  // State
  const [users, setUsers] = useState<types.UserListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<types.UserRole | ''>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState<FormState>({ email: '', password: '', role: '' });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [rowAction, setRowAction] = useState<RowAction>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refreshUsers = useCallback(async () => {
    // Include inactive users so they can be shown (badged) and reactivated.
    const list = await apiClient.listUsers(true);
    setUsers(list);
  }, [apiClient]);

  // Load data
  useEffect(() => {
    if (!canAccess) {
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await refreshUsers();
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to load users'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [canAccess, refreshUsers]);

  /** Whether the signed-in user may edit / deactivate / reset this row. */
  const canManageRow = (target: types.UserListItem) => canManageUserRole(actorRole, target.role);
  const isSelf = (target: types.UserListItem) => target.id === user?.id;

  const normalizedSearch = search.trim().toLowerCase();
  const visibleUsers = users
    .filter((u) => !normalizedSearch || u.email.toLowerCase().includes(normalizedSearch))
    .filter((u) => !roleFilter || u.role === roleFilter)
    .filter((u) => statusFilter === 'all' || (statusFilter === 'active' ? u.isActive : !u.isActive))
    .sort((a, b) => a.email.localeCompare(b.email));

  // Role filter lists every role that could appear, including legacy OFFICE rows.
  const filterRoles = Object.values(types.UserRole).filter(
    (role) => role !== types.UserRole.OFFICE || users.some((u) => u.role === role)
  );

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const clearRowAction = () => {
    setRowAction(null);
    setResetPassword('');
    setResetError(null);
  };

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm({
      email: '',
      password: '',
      // TECHNICIAN is assignable by every role that can reach this page.
      role: assignableRoles.includes(types.UserRole.TECHNICIAN) ? types.UserRole.TECHNICIAN : '',
    });
    setFormError(null);
    setNotice(null);
    clearRowAction();
  };

  const openEdit = (target: types.UserListItem) => {
    setEditing({ mode: 'edit', target });
    setForm({ email: target.email, password: '', role: target.role });
    setFormError(null);
    setNotice(null);
    clearRowAction();
  };

  const closeForm = () => {
    setEditing(null);
    setForm({ email: '', password: '', role: '' });
    setFormError(null);
  };

  // Role options for the form: what the actor may assign, plus the target's
  // current role when it is not assignable (e.g. legacy OFFICE) so the select
  // can display it. An unchanged role is never sent back.
  const editingTarget = editing?.mode === 'edit' ? editing.target : null;
  const roleOptions: types.UserRole[] =
    editingTarget && !assignableRoles.includes(editingTarget.role)
      ? [editingTarget.role, ...assignableRoles]
      : assignableRoles;
  const roleLocked = editingTarget !== null && isSelf(editingTarget);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;

    const email = form.email.trim();
    if (!email) {
      setFormError('Email is required');
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setFormError('Enter a valid email address');
      return;
    }
    if (!form.role) {
      setFormError('Select a role');
      return;
    }

    try {
      if (editing.mode === 'create') {
        const strengthError = getPasswordStrengthError(form.password);
        if (strengthError) {
          setFormError(strengthError);
          return;
        }
        if (!canManageUserRole(actorRole, form.role)) {
          setFormError(`You cannot create ${roleLabel(form.role)} users`);
          return;
        }

        setIsSaving(true);
        setFormError(null);
        const created = await apiClient.createUser({ email, password: form.password, role: form.role });
        setNotice(`Created ${created.email}. They must change their temporary password at first sign-in.`);
      } else {
        // PATCH only what changed (an unchanged legacy role would be rejected).
        const request: types.UpdateUserRequest = {};
        if (email !== editing.target.email) request.email = email;
        if (form.role !== editing.target.role) {
          if (!canManageUserRole(actorRole, form.role)) {
            setFormError(`You cannot assign the ${roleLabel(form.role)} role`);
            return;
          }
          request.role = form.role;
        }

        if (Object.keys(request).length === 0) {
          closeForm();
          return;
        }

        setIsSaving(true);
        setFormError(null);
        const updated = await apiClient.updateUser(editing.target.id, request);
        setNotice(`Updated ${updated.email}.`);
      }

      closeForm();
      await refreshUsers();
    } catch (err) {
      // e.g. 400 DUPLICATE_USER, 403 INSUFFICIENT_PERMISSIONS, 409 LAST_ADMIN,
      // or a validation message.
      setFormError(
        getErrorMessage(err, editing.mode === 'edit' ? 'Failed to update user' : 'Failed to create user')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (target: types.UserListItem) => {
    try {
      setBusyId(target.id);
      setError(null);
      setNotice(null);

      if (target.isActive) {
        await apiClient.deactivateUser(target.id);
        setNotice(`Deactivated ${target.email}.`);
      } else {
        await apiClient.updateUser(target.id, { isActive: true });
        setNotice(`Reactivated ${target.email}.`);
      }

      clearRowAction();
      if (editingTarget?.id === target.id) closeForm();
      await refreshUsers();
    } catch (err) {
      // e.g. 409 LAST_ADMIN "Cannot remove the last active admin for this tenant"
      clearRowAction();
      setError(getErrorMessage(err, target.isActive ? 'Failed to deactivate user' : 'Failed to reactivate user'));
    } finally {
      setBusyId(null);
    }
  };

  const handleResetPassword = async (target: types.UserListItem) => {
    const strengthError = getPasswordStrengthError(resetPassword);
    if (strengthError) {
      setResetError(strengthError);
      return;
    }

    try {
      setBusyId(target.id);
      setResetError(null);
      setError(null);
      setNotice(null);

      await apiClient.resetUserPassword(target.id, resetPassword);
      clearRowAction();
      setNotice(`Password reset for ${target.email}. They must change it at next sign-in.`);
      await refreshUsers();
    } catch (err) {
      setResetError(getErrorMessage(err, 'Failed to reset password'));
    } finally {
      setBusyId(null);
    }
  };

  if (!canAccess) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/settings" className="text-sm font-semibold text-gray-500 hover:text-blue-600">
          ← Settings
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1 mb-6">Users</h1>
        <section className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-600">You don't have permission to manage users.</p>
        </section>
      </main>
    );
  }

  const columnCount = 5;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div>
          <Link to="/settings" className="text-sm font-semibold text-gray-500 hover:text-blue-600">
            ← Settings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1">Users</h1>
        </div>
        {!editing && assignableRoles.length > 0 && (
          <button onClick={openCreate} className={primaryButtonClass}>
            Add User
          </button>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {notice && <Toast kind="success">{notice}</Toast>}

      {/* Add / Edit Form */}
      {editing && (
        <section className="bg-white rounded-lg shadow mb-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">
                {editing.mode === 'edit' ? `Edit ${editing.target.email}` : 'New User'}
              </h2>

              {formError && <Toast kind="error">{formError}</Toast>}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <div className={editing.mode === 'create' ? 'sm:col-span-2' : 'sm:col-span-4'}>
                  <label htmlFor="userEmail" className={labelClass}>
                    Email<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <input
                    id="userEmail"
                    type="email"
                    autoComplete="off"
                    value={form.email}
                    onChange={(e) => setField('email', e.target.value)}
                    disabled={isSaving}
                    placeholder="name@example.com"
                    className={inputClass}
                  />
                </div>

                {editing.mode === 'create' && (
                  <div className="sm:col-span-2">
                    <label htmlFor="userPassword" className={labelClass}>
                      Temporary password<span className="ml-0.5 text-red-600">*</span>
                    </label>
                    <input
                      id="userPassword"
                      type="password"
                      autoComplete="new-password"
                      value={form.password}
                      onChange={(e) => setField('password', e.target.value)}
                      disabled={isSaving}
                      aria-describedby="userPasswordHint"
                      className={inputClass}
                    />
                    <p id="userPasswordHint" className="mt-1.5 text-xs text-gray-500">
                      {PASSWORD_REQUIREMENTS_HINT} The user must change it at first sign-in.
                    </p>
                  </div>
                )}

                <div className="sm:col-span-2">
                  <label htmlFor="userRole" className={labelClass}>
                    Role<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <select
                    id="userRole"
                    value={form.role}
                    onChange={(e) => setField('role', e.target.value as types.UserRole)}
                    disabled={isSaving || roleLocked}
                    className={inputClass}
                  >
                    {form.role === '' && <option value="">Select a role</option>}
                    {roleOptions.map((role) => (
                      <option key={role} value={role} disabled={!assignableRoles.includes(role)}>
                        {roleLabel(role)}
                      </option>
                    ))}
                  </select>
                  {roleLocked && (
                    <p className="mt-1.5 text-xs text-gray-500">You can't change your own role.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
              <button type="button" onClick={closeForm} disabled={isSaving} className={secondaryButtonClass}>
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className={primaryButtonClass}>
                {isSaving ? 'Saving...' : editing.mode === 'edit' ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-4 sm:p-6">
        {/* Filters */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-6 mb-4">
          <div className="sm:col-span-3">
            <label htmlFor="userSearch" className={labelClass}>
              Search
            </label>
            <input
              id="userSearch"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email"
              className={inputClass}
            />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="userRoleFilter" className={labelClass}>
              Role
            </label>
            <select
              id="userRoleFilter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as types.UserRole | '')}
              className={inputClass}
            >
              <option value="">All roles</option>
              {filterRoles.map((role) => (
                <option key={role} value={role}>
                  {roleLabel(role)}
                </option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-1">
            <label htmlFor="userStatusFilter" className={labelClass}>
              Status
            </label>
            <select
              id="userStatusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className={inputClass}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Table */}
        {!isLoading &&
          (users.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No users yet</p>
          ) : visibleUsers.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No users match the current filters</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {visibleUsers.map((target) => {
                    const manageable = canManageRow(target);
                    const self = isSelf(target);
                    const isBusy = busyId === target.id;
                    const confirmingToggle =
                      rowAction?.kind === 'toggleActive' && rowAction.userId === target.id;
                    const resetting = rowAction?.kind === 'resetPassword' && rowAction.userId === target.id;
                    const lockedReason = !manageable
                      ? `Only admins can manage ${roleLabel(target.role)} users`
                      : undefined;

                    return (
                      <React.Fragment key={target.id}>
                        <tr className={target.isActive ? undefined : 'bg-gray-50'}>
                          <td data-label="Email" data-primary className="px-4 py-3 min-w-0">
                            <span className={`font-semibold break-words min-w-0 ${target.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                              {target.email}
                            </span>
                            {self && <span className="ml-2 text-xs text-gray-500">(you)</span>}
                          </td>
                          <td data-label="Role" className="px-4 py-3 text-sm text-gray-900">{roleLabel(target.role)}</td>
                          <td data-label="Status" className="px-4 py-3 text-sm">
                            <span className="inline-flex flex-wrap justify-end md:justify-start gap-1.5">
                              {target.isActive ? (
                                <span className={`${badgeBase} bg-green-100 text-green-800`}>Active</span>
                              ) : (
                                <span className={`${badgeBase} bg-gray-200 text-gray-700`}>Inactive</span>
                              )}
                              {target.mustChangePassword && (
                                <span
                                  className={`${badgeBase} bg-yellow-100 text-yellow-800`}
                                  title="Signed-in access is blocked until this user sets a new password"
                                >
                                  Password change pending
                                </span>
                              )}
                            </span>
                          </td>
                          <td data-label="Created" className="px-4 py-3 text-sm text-gray-600">
                            {new Date(target.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right md:whitespace-nowrap">
                            {confirmingToggle ? (
                              <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="text-sm text-gray-700">
                                  {target.isActive ? 'Deactivate this user?' : 'Reactivate this user?'}
                                </span>
                                <button
                                  onClick={() => handleToggleActive(target)}
                                  disabled={isBusy}
                                  className={`${target.isActive ? rowDangerActionClass : rowActionClass} min-h-[44px] md:min-h-0`}
                                >
                                  {isBusy ? 'Saving...' : 'Confirm'}
                                </button>
                                <button onClick={clearRowAction} disabled={isBusy} className={`${rowActionClass} min-h-[44px] md:min-h-0`}>
                                  Cancel
                                </button>
                              </span>
                            ) : manageable ? (
                              <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-1">
                                <button
                                  onClick={() => openEdit(target)}
                                  disabled={isSaving || busyId !== null}
                                  className={`${rowActionClass} min-h-[44px] md:min-h-0`}
                                >
                                  Edit
                                </button>
                                {/* Own account: use Change password instead, and
                                    don't lock yourself out by deactivating. */}
                                {!self && (
                                  <>
                                    <button
                                      onClick={() => {
                                        clearRowAction();
                                        setNotice(null);
                                        setRowAction({ kind: 'resetPassword', userId: target.id });
                                      }}
                                      disabled={busyId !== null}
                                      className={`${rowActionClass} min-h-[44px] md:min-h-0`}
                                    >
                                      Reset password
                                    </button>
                                    <button
                                      onClick={() => {
                                        clearRowAction();
                                        setNotice(null);
                                        setRowAction({ kind: 'toggleActive', userId: target.id });
                                      }}
                                      disabled={busyId !== null}
                                      className={`${target.isActive ? rowDangerActionClass : rowActionClass} min-h-[44px] md:min-h-0`}
                                    >
                                      {target.isActive ? 'Deactivate' : 'Reactivate'}
                                    </button>
                                  </>
                                )}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400" title={lockedReason}>
                                No access
                              </span>
                            )}
                          </td>
                        </tr>

                        {/* Inline password reset */}
                        {resetting && (
                          <tr className="bg-gray-50">
                            <td colSpan={columnCount} className="px-4 py-4">
                              <form
                                noValidate
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  handleResetPassword(target);
                                }}
                              >
                                <label htmlFor={`resetPassword-${target.id}`} className={labelClass}>
                                  New temporary password for {target.email}
                                </label>
                                <div className="flex flex-wrap items-center gap-3">
                                  <input
                                    id={`resetPassword-${target.id}`}
                                    type="password"
                                    autoComplete="new-password"
                                    value={resetPassword}
                                    onChange={(e) => setResetPassword(e.target.value)}
                                    disabled={isBusy}
                                    aria-describedby={`resetPasswordHint-${target.id}`}
                                    className={`${inputClass} sm:max-w-xs`}
                                    autoFocus
                                  />
                                  <button type="submit" disabled={isBusy} className={primaryButtonClass}>
                                    {isBusy ? 'Resetting...' : 'Reset Password'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={clearRowAction}
                                    disabled={isBusy}
                                    className={secondaryButtonClass}
                                  >
                                    Cancel
                                  </button>
                                </div>
                                <p id={`resetPasswordHint-${target.id}`} className="mt-1.5 text-xs text-gray-500">
                                  {PASSWORD_REQUIREMENTS_HINT} The user will be required to change it at next sign-in.
                                </p>
                                {resetError && <Toast kind="error">{resetError}</Toast>}
                              </form>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </section>
      {/* Two-step verification status and reset (ADMIN; tracker item 63) */}
      {canManageMfaPolicy(user) && !isLoading && users.length > 0 && <UserMfaPanel users={users} currentUserId={user?.id} />}
    </main>
  );
}
